import { saveAs } from 'file-saver';

import { FIOPattern, EmailPattern, UrlPattern } from './constants';
import { SDK_VERSION } from './constants';
import { IS_DEVELOPMENT } from './envs';

import { MetaProps, metaMap, defaultMeta } from 'components/layout/PageMeta';

import { AddressType } from 'graphql/types';

export const getRemainingTimeInSeconds = (timestamp: number) => {
  const diff = new Date(timestamp * 1000).getTime() - new Date().getTime();

  return diff / 1000;
};

export const validateEmail = (email: string): boolean =>
  EmailPattern.test(email);

export const validateUrl = (url: string): boolean => UrlPattern.test(url);

export const validatePhoneNumber = (phoneNumber: string): boolean =>
  phoneNumber.replace(/\D+/g, '').length === 11;

export const validateSpaces = (value: string): boolean =>
  /^(?:[\S]*)$/.test(value) || value.trim().length > 0;

// валидация полей с именем/фамилией/отчеством
export const validateFIO = (value: string): boolean => FIOPattern.test(value);

// валидация для полей соц сетей
export const validateSocialUrl = (url: string): boolean => {
  if (!url) return true;
  return validateUrl(url) || url.startsWith('@');
};

// https://proweb63.ru/help/js/declension-in-js
export const decl = (number: number, txt: string[]) => {
  const cases = [2, 0, 1, 1, 1, 2];

  return txt[
    number % 100 > 4 && number % 100 < 20
      ? 2
      : cases[number % 10 < 5 ? number % 10 : 5]
  ];
};

// замена символов переноса строк на определенный символ
export const getFormattedText = (str: string, char: string) =>
  str.replace(/(?:\r\n|\r|\n)/g, char);

// https://stackoverflow.com/questions/15900485/correct-way-to-convert-size-in-bytes-to-kb-mb-gb-in-javascript
export const formatBytes = (bytes: number, decimals: number = 2) => {
  if (bytes === 0) return '0 Байт';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Байт', 'КБ', 'МБ', 'ГБ', 'ТБ', 'ПБ', 'EB', 'ZB', 'YB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

//сортировка массива в рандомном порядке и обрезка его до указанной длины
export const shuffleAndSliceArray = <T>(
  items: Array<T>,
  slicedCount?: number
): Array<T> =>
  [...items]
    .sort(() => Math.random() - 0.5)
    .slice(0, slicedCount ?? items.length - 1);

export const urlSafeBase64 = (string: string) =>
  Buffer.from(string)
    .toString('base64')
    .replace(/=/g, '')
    .replace(/\+/g, '-')
    .replace(/\//g, '_');

//сохранят файл в файловую систему пользователя
export const saveFile = (url: string | Blob, fileName: string = '') => {
  saveAs(url, fileName);
};

// преобразует данные в pdf формат
export const convertToPDFAndSave = (data: any) => {
  const blob = new Blob([data], {
    type: 'application/pdf',
  });
  const url = window.URL.createObjectURL(blob);

  window.open(url);

  saveFile(blob);
};

// получает заголовок и описание на основе урла
export const getMetaInfo = (url: string): MetaProps => {
  const key = url as keyof typeof metaMap;

  const { title, description, image, canonicalHref } = metaMap[key] || {
    title: defaultMeta.title,
    description: defaultMeta.description,
    image: defaultMeta.image,
    canonicalHref: defaultMeta.canonicalHref,
  };

  return {
    title,
    description,
    image,
    canonicalHref,
  };
};

export const mapUserAddress = (address: AddressType | null | undefined) => {
  if (address) {
    return { ...address, __typename: undefined };
  }

  return null;
};

export const isFileSizeExceedMaxSize = (
  size: number,
  maxSize?: number
): boolean => {
  const BYTES_PER_MEGA_BYTES = 1e6;

  if (!maxSize) {
    return false;
  }
  const convertedMaxSize = maxSize * BYTES_PER_MEGA_BYTES;

  return size > convertedMaxSize;
};

export const upperCaseFirst = (value: string): string => {
  return value.charAt(0).toUpperCase() + value.substr(1);
};

export const snakeToCamelCase = (str: string): string =>
  str
    .toLowerCase()
    .replace(/([-_][a-z])/g, (group) => group.toUpperCase().replace('_', ''));

export const buildSdkUrl = (fileType: 'js' | 'css') => {
  const host = process.env.NEXT_PUBLIC_STATIC_HOST;
  let stand = process.env.NEXT_PUBLIC_WERF_ENV;
  const version = SDK_VERSION;

  if (IS_DEVELOPMENT) {
    stand = 'dev';
  }

  return `${host}/${stand}/js-sdk/${version}/${fileType}/main.${fileType}`;
};
